import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REVIEW_COMMENT_GROUP_CATALOG,
  type ReviewList,
  type ReviewSeverity,
  type UpdateReviewSettingsInput,
} from '@lizrd/contracts';
import { getReviewSettings, updateReviewSettings } from '../lib/api';
import { Alert } from './ui/alert';
import { Card } from './ui/card';
import { Spinner } from './ui/spinner';
import { Toggle } from './ui/toggle';
import { Select } from './ui/select';
import { Input } from './ui/input';

const SEVERITY_OPTIONS: { value: ReviewSeverity; label: string }[] = [
  { value: 'low', label: 'Low — surface everything' },
  { value: 'medium', label: 'Medium and above' },
  { value: 'high', label: 'High only' },
];

/**
 * Per-domain CI/CD Reviews settings (admins & managers). The master switch turns
 * PR reviews on/off; when on, the team scopes which repositories Lizrd reviews and
 * tunes how noisy the comments are (minimum severity + a cost-increase threshold).
 */
export function ReviewSettings({ domainId }: { domainId: string }) {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['review-settings', domainId],
    queryFn: () => getReviewSettings(domainId),
    enabled: !!domainId,
  });
  const save = useMutation({
    mutationFn: (patch: UpdateReviewSettingsInput) => updateReviewSettings(domainId, patch),
    onSuccess: (updated) => {
      qc.setQueryData(['review-settings', domainId], updated);
      // Synchronously flip `enabled` in the Reviews page's cache so its
      // educational/enabled state is correct even when that page renders from
      // cache (not just after a refetch) — this is what makes the toggle reflect
      // without a hard reload. The invalidate below then refreshes the rest.
      qc.setQueryData<ReviewList | undefined>(['reviews', domainId], (old) =>
        old ? { ...old, enabled: updated.enabled } : old,
      );
      qc.invalidateQueries({ queryKey: ['reviews', domainId] });
      // Keep the domain detail + list fresh too (they carry reviewsEnabled).
      qc.invalidateQueries({ queryKey: ['domain', domainId] });
      qc.invalidateQueries({ queryKey: ['domains'] });
    },
  });

  const s = settings.data;
  const enabled = s?.enabled ?? false;
  const busy = settings.isLoading || save.isPending;

  // Cost threshold is a free-typed number — keep local text state, commit on blur.
  const [threshold, setThreshold] = useState('');
  useEffect(() => {
    if (s) setThreshold(String(s.costThresholdMonthly));
  }, [s?.costThresholdMonthly]);

  const selectedRepos = new Set(s?.repositories ?? []);
  const toggleRepo = (repo: string) => {
    const all = s?.availableRepositories ?? [];
    // An empty stored list means "all repos"; expand it so unchecking one yields
    // "all except that one" rather than "only that one".
    const effective = selectedRepos.size === 0 ? new Set(all) : new Set(selectedRepos);
    if (effective.has(repo)) effective.delete(repo);
    else effective.add(repo);
    // If every repo is selected again, store [] (= all) so newly-discovered repos
    // are included by default.
    save.mutate({ repositories: effective.size === all.length ? [] : [...effective] });
  };

  const commitThreshold = () => {
    const n = Number(threshold);
    if (!Number.isFinite(n) || n < 0) {
      setThreshold(String(s?.costThresholdMonthly ?? 0));
      return;
    }
    if (n !== s?.costThresholdMonthly) save.mutate({ costThresholdMonthly: n });
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-display text-base font-bold text-bone">Pull-request reviews</h3>
        <p className="text-xs text-muted">
          Lizrd reviews infrastructure pull requests on GitHub &amp; GitLab and posts the cost delta
          and waste anti-patterns — grounded in your knowledge graph — before they merge.
        </p>
      </div>

      {enabled && s?.writeAccess && !s.writeAccess.canPost && (
        <Alert variant="warning">
          <div className="text-sm font-semibold text-bone">
            Reviews aren&apos;t fully set up yet
          </div>
          <p className="mt-0.5 text-xs leading-relaxed">{s.writeAccess.detail}</p>
          {s.writeAccess.actionUrl ? (
            <a
              href={s.writeAccess.actionUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1.5 inline-block text-xs font-semibold text-iris hover:underline"
            >
              Set up the Lizrd Reviews app →
            </a>
          ) : (
            <Link
              to={`/domains/${domainId}/integrations`}
              className="mt-1.5 inline-block text-xs font-semibold text-iris hover:underline"
            >
              {s.writeAccess.provider === 'GITLAB'
                ? 'Update your GitLab token →'
                : 'Open integrations →'}
            </Link>
          )}
        </Alert>
      )}

      <Card className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-bone">Review infrastructure PRs</div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted">
              When on, Lizrd comments on IaC pull requests and the findings appear on the Reviews
              page. Read-only to your code; it only posts checks and comments.
            </p>
          </div>
          <div className="flex flex-none items-center gap-2 pt-0.5">
            {busy && <Spinner size="sm" />}
            <Toggle
              checked={enabled}
              disabled={busy}
              onChange={(v) => save.mutate({ enabled: v })}
              label="Enable pull-request reviews"
            />
          </div>
        </div>

        {enabled && s && (
          <div className="mt-4 space-y-5 border-t border-hairline pt-4">
            {/* Minimum severity */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-bone">Minimum comment severity</div>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">
                  Only surface findings at or above this severity. Keeps the PR comment quiet.
                </p>
              </div>
              <div className="w-48 flex-none">
                <Select
                  value={s.minSeverity}
                  onChange={(v) => save.mutate({ minSeverity: v })}
                  options={SEVERITY_OPTIONS}
                  ariaLabel="Minimum comment severity"
                  disabled={busy}
                />
              </div>
            </div>

            {/* Cost threshold */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-bone">Cost-increase threshold</div>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">
                  Only flag a cost increase at or above this monthly amount. Set 0 to flag any
                  increase.
                </p>
              </div>
              <div className="flex w-48 flex-none items-center gap-1.5">
                <span className="text-sm text-muted">$</span>
                <Input
                  type="number"
                  min={0}
                  step={10}
                  value={threshold}
                  disabled={busy}
                  onChange={(e) => setThreshold(e.target.value)}
                  onBlur={commitThreshold}
                  aria-label="Cost-increase threshold (USD per month)"
                />
                <span className="whitespace-nowrap text-xs text-muted">/mo</span>
              </div>
            </div>

            {/* Comment types */}
            <div>
              <div className="text-sm font-semibold text-bone">Comment types</div>
              <p className="mt-0.5 text-xs leading-relaxed text-muted">
                Choose which classes of comment Lizrd makes on your pull requests.
              </p>
              <div className="mt-2 overflow-hidden rounded-md border border-hairline">
                {REVIEW_COMMENT_GROUP_CATALOG.map((g, i) => (
                  <div
                    key={g.key}
                    className={`flex items-start justify-between gap-4 px-3 py-2.5 ${
                      i > 0 ? 'border-t border-hairline' : ''
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-bone">{g.label}</div>
                      <p className="mt-0.5 text-xs leading-relaxed text-muted">{g.description}</p>
                    </div>
                    <div className="flex-none pt-0.5">
                      <Toggle
                        checked={s.commentGroups[g.key]}
                        disabled={busy}
                        onChange={(v) =>
                          save.mutate({ commentGroups: { ...s.commentGroups, [g.key]: v } })
                        }
                        label={`Toggle ${g.label} comments`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Repositories */}
            <div>
              <div className="text-sm font-semibold text-bone">Repositories</div>
              <p className="mt-0.5 text-xs leading-relaxed text-muted">
                {s.availableRepositories.length === 0
                  ? 'No repositories discovered yet — Lizrd reviews all connected repositories by default.'
                  : selectedRepos.size === 0
                    ? 'All repositories are reviewed. Select specific ones to limit the scope.'
                    : `Reviewing ${selectedRepos.size} of ${s.availableRepositories.length} repositories.`}
              </p>
              {s.availableRepositories.length > 0 && (
                <div className="mt-2 overflow-hidden rounded-md border border-hairline">
                  {s.availableRepositories.map((repo, i) => (
                    <label
                      key={repo}
                      className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2.5 transition-colors hover:bg-surface-2/50 ${
                        i > 0 ? 'border-t border-hairline' : ''
                      }`}
                    >
                      <span className="font-mono text-sm text-bone">{repo}</span>
                      <input
                        type="checkbox"
                        checked={selectedRepos.size === 0 || selectedRepos.has(repo)}
                        disabled={busy}
                        onChange={() => toggleRepo(repo)}
                        className="h-4 w-4 flex-none rounded-sm border border-border bg-surface accent-iris focus:outline-none focus-visible:ring-2 focus-visible:ring-iris"
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
